import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import JSZip from 'jszip';
import QRCode from 'react-qr-code';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  QrCode, 
  Download, 
  Copy, 
  RefreshCw, 
  Share, 
  ExternalLink,
  Link,
  Trash2,
  CheckCircle,
  XCircle,
  Archive,
  RotateCcw,
  Settings
} from 'lucide-react';
import { toast } from "@/hooks/ui/use-toast";

interface CouponOffer {
  id: string;
  business_id: string;
  name: string;
  header?: string;
  description?: string;
  discount_type: 'percentage' | 'fixed_amount' | 'free_item';
  discount_value?: number;
  minimum_purchase?: number;
  valid_from: string;
  valid_until?: string;
  usage_limit_per_customer: number;
  total_usage_limit?: number;
  background_color?: string;
  logo_url?: string;
  hero_image_url?: string;
  google_class_id?: string;
  is_active: boolean;
  auto_delete_used_tokens?: boolean;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

interface CouponToken {
  id: string;
  token: string;
  usage_limit: number;
  current_uses: number;
  label?: string;
  is_active: boolean;
  expires_at?: string;
  created_at: string;
  created_by: string;
}

interface ShareDownloadDialogProps {
  couponOffer: CouponOffer;
  isOpen: boolean;
  onClose: () => void;
}

const BASE_URL = 'https://taginn.app';

const USAGE_PRESETS = [
  { label: '1 use', value: 1 },
  { label: '5 uses', value: 5 },
  { label: '10 uses', value: 10 },
  { label: '25 uses', value: 25 },
  { label: '50 uses', value: 50 },
  { label: '100 uses', value: 100 },
  { label: 'Custom', value: 0 }
];

const ShareDownloadDialog: React.FC<ShareDownloadDialogProps> = ({
  couponOffer,
  isOpen,
  onClose
}) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Local state for auto-delete toggle (for optimistic updates)
  const [localAutoDeleteSetting, setLocalAutoDeleteSetting] = useState(couponOffer.auto_delete_used_tokens || false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // State for QR code hover preview
  const [hoveredQrToken, setHoveredQrToken] = useState<string | null>(null);
  const [qrPosition, setQrPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // Update local state when couponOffer prop changes
  React.useEffect(() => {
    setLocalAutoDeleteSetting(couponOffer.auto_delete_used_tokens || false);
  }, [couponOffer.auto_delete_used_tokens]);
  
  // State for token generation form
  const [usageLimit, setUsageLimit] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [label, setLabel] = useState('');
  const [customUsageLimit, setCustomUsageLimit] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('1');

  // Fetch existing tokens for this coupon offer
  const { data: tokens = [], isLoading: tokensLoading, refetch: refetchTokens } = useQuery({
    queryKey: ['coupon-tokens', couponOffer.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coupon_access_tokens')
        .select('*')
        .eq('coupon_offer_id', couponOffer.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as CouponToken[];
    },
    enabled: isOpen,
  });

  // Generate tokens mutation
  const generateTokensMutation = useMutation({
    mutationFn: async ({ 
      usage_limit, 
      quantity, 
      label 
    }: { 
      usage_limit: number; 
      quantity: number; 
      label?: string; 
    }) => {
      const response = await fetch('/functions/v1/generate-coupon-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': supabase.supabaseKey,
        },
        body: JSON.stringify({
          coupon_offer_id: couponOffer.id,
          usage_limit,
          quantity,
          label: label || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate tokens');
      }

      return response.json();
    },
    onSuccess: () => {
      toast.success('Coupon tokens generated successfully');
      refetchTokens();
      // Reset form
      setQuantity(1);
      setLabel('');
      setSelectedPreset('1');
      setUsageLimit(1);
    },
    onError: (error: Error) => {
      toast.error('Failed to generate tokens', {
        description: error.message
      });
    },
  });

  // Toggle token active status mutation
  const toggleTokenMutation = useMutation({
    mutationFn: async ({ tokenId, isActive }: { tokenId: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('coupon_access_tokens')
        .update({ is_active: !isActive })
        .eq('id', tokenId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Token status updated');
      refetchTokens();
    },
    onError: () => {
      toast.error('Failed to update token status');
    },
  });

  // Delete token mutation
  const deleteTokenMutation = useMutation({
    mutationFn: async (tokenId: string) => {
      const { error } = await supabase
        .from('coupon_access_tokens')
        .delete()
        .eq('id', tokenId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Token deleted successfully');
      refetchTokens();
    },
    onError: () => {
      toast.error('Failed to delete token');
    },
  });

  // Update auto-delete setting mutation
  const updateAutoDeleteMutation = useMutation({
    mutationFn: async (autoDelete: boolean) => {
      // Update the setting in database
      const { error } = await supabase
        .from('coupon_offers')
        .update({ auto_delete_used_tokens: autoDelete })
        .eq('id', couponOffer.id);

      if (error) throw error;

      // If enabling auto-delete, clean up already exhausted tokens
      let cleanedUpCount = 0;
      if (autoDelete) {
        // First fetch tokens that are exhausted, then delete them
        const { data: exhaustedTokens, error: fetchError } = await supabase
          .from('coupon_access_tokens')
          .select('id, current_uses, usage_limit')
          .eq('coupon_offer_id', couponOffer.id);

        if (!fetchError && exhaustedTokens) {
          const exhaustedTokenIds = exhaustedTokens
            .filter(token => token.current_uses >= token.usage_limit)
            .map(token => token.id);

          if (exhaustedTokenIds.length > 0) {
            const { error: cleanupError } = await supabase
              .from('coupon_access_tokens')
              .delete()
              .in('id', exhaustedTokenIds);

            if (cleanupError) {
              console.error('Failed to cleanup exhausted tokens:', cleanupError);
              // Don't fail the entire operation, just log the error
            } else {
              cleanedUpCount = exhaustedTokenIds.length;
              console.log(`Cleaned up ${cleanedUpCount} exhausted tokens`);
            }
          }
        }
      }

      return { autoDelete, cleanedUpCount };
    },
    onMutate: async (autoDelete) => {
      // Optimistic update
      setLocalAutoDeleteSetting(autoDelete);
    },
    onSuccess: (result) => {
      const { autoDelete, cleanedUpCount } = result;
      
      let message = autoDelete ? 'Auto-delete enabled' : 'Auto-delete disabled';
      if (autoDelete && cleanedUpCount > 0) {
        message += ` - ${cleanedUpCount} used token${cleanedUpCount > 1 ? 's' : ''} removed`;
      } else if (autoDelete) {
        message += ' - future used tokens will be automatically removed';
      }
      
      toast.success(message);
      
      // Invalidate multiple query keys to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['coupon-offers'] });
      queryClient.invalidateQueries({ queryKey: ['coupon-tokens', couponOffer.id] });
      // Refetch tokens to show immediate cleanup results
      refetchTokens();
    },
    onError: (error, autoDelete) => {
      // Revert optimistic update
      setLocalAutoDeleteSetting(!autoDelete);
      toast.error('Failed to update auto-delete setting');
    },
  });

  const handlePresetChange = useCallback((value: string) => {
    setSelectedPreset(value);
    if (value === '0') { // Custom preset
      // If no custom value set, default to 10
      if (!customUsageLimit) {
        setCustomUsageLimit('10');
        setUsageLimit(10);
      } else {
        setUsageLimit(parseInt(customUsageLimit) || 1);
      }
    } else {
      const preset = USAGE_PRESETS.find(p => p.value.toString() === value);
      if (preset) {
        setUsageLimit(preset.value);
      }
    }
  }, [customUsageLimit]);

  const handleCustomUsageLimitChange = useCallback((value: string) => {
    setCustomUsageLimit(value);
    if (selectedPreset === '0') { // Custom preset
      const numValue = parseInt(value) || 1;
      setUsageLimit(Math.max(1, Math.min(1000, numValue)));
    }
  }, [selectedPreset]);

  const handleGenerateTokens = useCallback(() => {
    if (quantity < 1 || quantity > 1000) {
      toast.error('Quantity must be between 1 and 1000');
      return;
    }
    if (usageLimit < 1 || usageLimit > 1000) {
      toast.error('Usage limit must be between 1 and 1000');
      return;
    }

    generateTokensMutation.mutate({
      usage_limit: usageLimit,
      quantity,
      label: label.trim() || undefined,
    });
  }, [usageLimit, quantity, label, generateTokensMutation]);

  const copyTokenLink = useCallback((token: string) => {
    const url = `${BASE_URL}/coupon-token/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Token link copied to clipboard');
  }, []);

  const copyQRCode = useCallback(async (token: string) => {
    try {
      const redemptionUrl = `${BASE_URL}/coupon-token/${token}`;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(redemptionUrl)}`;

      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `coupon-token-${token.slice(0, 8)}-qr.png`;
      link.click();

      URL.revokeObjectURL(url);
      toast.success('QR code downloaded');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  }, []);

  const downloadAllQRCodes = useCallback(async () => {
    if (tokens.length === 0) {
      toast.error('No tokens available to download');
      return;
    }

    try {
      const zip = new JSZip();
      
      // Add a readme file
      zip.file("README.txt", 
        `Coupon QR Codes for: ${couponOffer.name}\n\n` +
        `Generated: ${new Date().toLocaleString()}\n` +
        `Total tokens: ${tokens.length}\n\n` +
        `Each QR code represents a unique coupon link that customers can scan.\n` +
        `Track usage in your TagInn dashboard.`
      );

      // Process tokens in batches to avoid overwhelming the API
      const batchSize = 10;
      for (let i = 0; i < tokens.length; i += batchSize) {
        const batch = tokens.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (token) => {
          try {
            const redemptionUrl = `${BASE_URL}/coupon-token/${token.token}`;
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(redemptionUrl)}`;

            const response = await fetch(qrUrl);
            const blob = await response.blob();

            const filename = token.label
              ? `${token.label.replace(/[^a-zA-Z0-9]/g, '_')}-${token.token.slice(0, 8)}.png`
              : `token-${token.token.slice(0, 8)}.png`;

            zip.file(filename, blob);
          } catch (error) {
            console.error(`Failed to process token ${token.token}:`, error);
          }
        }));
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${couponOffer.name.replace(/[^a-zA-Z0-9]/g, '_')}-coupon-qr-codes.zip`;
      link.click();
      
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${tokens.length} QR codes as ZIP file`);
    } catch (error) {
      console.error('Error creating ZIP file:', error);
      toast.error('Failed to create ZIP file');
    }
  }, [tokens, couponOffer.name]);


  const shortenToken = useCallback((token: string) => {
    return `${token.slice(0, 8)}...${token.slice(-4)}`;
  }, []);

  const handleAutoDeleteToggle = useCallback((checked: boolean) => {
    if (checked) {
      // Show confirmation when enabling
      setShowConfirmDialog(true);
    } else {
      // Disable immediately without confirmation
      updateAutoDeleteMutation.mutate(false);
    }
  }, [updateAutoDeleteMutation]);

  const handleConfirmAutoDelete = useCallback(() => {
    setShowConfirmDialog(false);
    updateAutoDeleteMutation.mutate(true);
  }, [updateAutoDeleteMutation]);

  const handleCancelAutoDelete = useCallback(() => {
    setShowConfirmDialog(false);
    // No need to revert anything since we never changed the state
  }, []);

  const handleQrHover = useCallback((token: string, element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    const tooltipHeight = 260; // QR code + text + padding

    // Calculate center of the button
    const buttonCenterX = rect.left + (rect.width / 2);

    setQrPosition({
      top: rect.top - tooltipHeight - 16, // Position above the button with 16px gap
      left: buttonCenterX // Position at button center, will use transform to center tooltip
    });
    setHoveredQrToken(token);
  }, []);

  const handleQrLeave = useCallback(() => {
    setHoveredQrToken(null);
    setQrPosition(null);
  }, []);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share className="h-5 w-5" />
            Share & Download - {couponOffer.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="max-w-4xl mx-auto">
          {/* Limited-Use Links Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="h-5 w-5" />
                Limited-Use Links
              </CardTitle>
              <CardDescription>
                Generate unique tokens with usage limits for targeted campaigns
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Auto-Delete Setting */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    <Label htmlFor="auto-delete-toggle" className="text-sm font-medium">
                      Auto-delete used tokens
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Automatically delete tokens when they reach their usage limit
                  </p>
                </div>
                <Switch
                  id="auto-delete-toggle"
                  checked={localAutoDeleteSetting}
                  onCheckedChange={handleAutoDeleteToggle}
                  disabled={updateAutoDeleteMutation.isPending}
                />
              </div>

              {/* Generation Form */}
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="usage-limit">Usage Limit</Label>
                    <Select value={selectedPreset} onValueChange={handlePresetChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {USAGE_PRESETS.map(preset => (
                          <SelectItem key={preset.value} value={preset.value.toString()}>
                            {preset.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedPreset === '0' && (
                      <Input
                        type="number"
                        placeholder="Enter custom limit"
                        value={customUsageLimit}
                        onChange={(e) => handleCustomUsageLimitChange(e.target.value)}
                        min={1}
                        max={1000}
                        className="mt-2"
                      />
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(1000, parseInt(e.target.value) || 1)))}
                      min={1}
                      max={1000}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="label">Label (Optional)</Label>
                  <Input
                    id="label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g., Holiday Campaign, VIP Customers"
                    maxLength={50}
                  />
                </div>
                
                <Button
                  onClick={handleGenerateTokens}
                  disabled={generateTokensMutation.isPending}
                  className="w-full"
                >
                  {generateTokensMutation.isPending ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Link className="h-4 w-4 mr-2" />
                  )}
                  Generate {quantity} Token{quantity > 1 ? 's' : ''}
                </Button>
              </div>

              {/* Bulk Actions */}
              {tokens.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    onClick={downloadAllQRCodes}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    Download All QR ({tokens.length})
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Generated Tokens Table */}
        {tokens.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Generated Tokens ({tokens.length})</CardTitle>
              <CardDescription>
                Manage your limited-use coupon tokens
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tokensLoading ? (
                <div className="text-center py-8">Loading tokens...</div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Token</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Uses</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tokens.map((token) => (
                        <TableRow key={token.id}>
                          <TableCell className="font-mono text-xs break-all max-w-xs">
                            {token.token}
                          </TableCell>
                          <TableCell>
                            {token.label ? (
                              <Badge variant="outline">{token.label}</Badge>
                            ) : (
                              <span className="text-gray-400">No label</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              {token.current_uses}/{token.usage_limit}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {token.is_active ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                              <span className="text-sm">
                                {token.is_active ? 'Active' : 'Disabled'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyTokenLink(token.token)}
                                title="Copy link"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <div
                                ref={(el) => {
                                  if (el) {
                                    buttonRefs.current.set(token.token, el);
                                  } else {
                                    buttonRefs.current.delete(token.token);
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  const element = buttonRefs.current.get(token.token);
                                  if (element) handleQrHover(token.token, element);
                                }}
                                onMouseLeave={handleQrLeave}
                              >
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => copyQRCode(token.token)}
                                  title="Download QR code"
                                >
                                  <QrCode className="h-4 w-4" />
                                </Button>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleTokenMutation.mutate({ 
                                  tokenId: token.id, 
                                  isActive: token.is_active 
                                })}
                                title={token.is_active ? 'Disable token' : 'Enable token'}
                                disabled={toggleTokenMutation.isPending}
                              >
                                {token.is_active ? (
                                  <XCircle className="h-4 w-4" />
                                ) : (
                                  <RotateCcw className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this token? This action cannot be undone.')) {
                                    deleteTokenMutation.mutate(token.id);
                                  }
                                }}
                                title="Delete token"
                                disabled={deleteTokenMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>

    {/* Auto-Delete Confirmation Dialog */}
    <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Enable Auto-Delete Used Tokens?</AlertDialogTitle>
          <AlertDialogDescription>
            This will automatically delete tokens when they reach their usage limit.
            Any existing tokens that are already fully used will be deleted immediately.
            <br /><br />
            <strong>This action cannot be undone for deleted tokens.</strong>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancelAutoDelete}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmAutoDelete}>
            Yes, Enable Auto-Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* QR Code Preview Portal */}
    {hoveredQrToken && qrPosition && createPortal(
      <div
        className="fixed z-[9999] pointer-events-none -translate-x-1/2"
        style={{
          top: `${qrPosition.top}px`,
          left: `${qrPosition.left}px`,
        }}
      >
        <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-2xl border-2 border-gray-300 dark:border-gray-600 p-4">
          <div className="bg-white p-2 rounded">
            <QRCode
              value={`${BASE_URL}/coupon-token/${hoveredQrToken}`}
              size={192}
              level="H"
            />
          </div>
          <div className="text-xs text-center text-gray-600 dark:text-gray-300 mt-2">
            Scan to redeem
          </div>
          {/* Arrow pointing down */}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[12px] border-r-[12px] border-t-[12px] border-l-transparent border-r-transparent border-t-white dark:border-t-gray-900"></div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-[-2px] w-0 h-0 border-l-[14px] border-r-[14px] border-t-[14px] border-l-transparent border-r-transparent border-t-gray-300 dark:border-t-gray-600"></div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default ShareDownloadDialog;